import { isValidElement } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";
import type { Role } from "../api/types";
import { ErrorBoundary } from "./ErrorBoundary";
import { NotFound } from "./NotFound";
import { Shell } from "./Shell";
import { RequireRole } from "../auth/RequireRole";
import { GroupDashboard } from "../features/dashboard/GroupDashboard";
import { CompaniesList } from "../features/companies/CompaniesList";
import { CompanyDetail } from "../features/companies/CompanyDetail";
import { DocumentsQueue } from "../features/documents/DocumentsQueue";
import { DocumentInspector } from "../features/documents/inspector/DocumentInspector";
import { InboundPage } from "../features/inbound/InboundPage";
import { InboundDocumentPage } from "../features/inbound/InboundDocumentPage";
import { MappingStudio } from "../features/hub/mapping/MappingStudio";
import { LegacyMappingRedirect } from "../features/hub/mapping/LegacyMappingRedirect";
import { OnboardWizard } from "../features/hub/onboard/OnboardWizard";
import { ReportsPage } from "../features/reports/ReportsPage";
import { ThemeSettingsPage } from "../features/settings/ThemeSettingsPage";
import { UsersPage } from "../features/users/UsersPage";
import { SettingsPage } from "../features/settings/SettingsPage";
import { AcceptInvite } from "../features/auth/AcceptInvite";
import { SignIn } from "../features/auth/SignIn";
import { Overview } from "../features/portal/Overview";
import { PortalDocumentDetail } from "../features/portal/PortalDocumentDetail";
import { PortalDocuments } from "../features/portal/PortalDocuments";
import { PortalLayout } from "../features/portal/PortalLayout";
import { PortalUsers } from "../features/portal/PortalUsers";
import { RequireEntityUser } from "../features/portal/RequireEntityUser";

// Screen errors stay inside the shell, so the sidebar survives a failed render.
const boundary = { errorElement: <ErrorBoundary /> };

export const routes: RouteObject[] = [
  { path: "/signin", element: <SignIn surface="hub" /> },
  { path: "/portal/signin", element: <SignIn surface="portal" /> },
  { path: "/accept", element: <AcceptInvite /> },
  {
    path: "portal",
    element: <RequireEntityUser />,
    ...boundary,
    children: [
      {
        element: <PortalLayout />,
        ...boundary,
        children: [
          { index: true, element: <Overview />, ...boundary, handle: { crumbs: ["Overview"] } },
          { path: "documents", element: <PortalDocuments />, ...boundary, handle: { crumbs: ["Documents"] } },
          {
            path: "documents/:docId",
            element: <PortalDocumentDetail />,
            ...boundary,
            handle: { crumbs: ["Documents", "Document"] },
          },
          { path: "users", element: <PortalUsers />, ...boundary, handle: { crumbs: ["Users"] } },
        ],
      },
    ],
  },
  {
    element: <RequireRole role="group_admin" />,
    ...boundary,
    children: [
      {
        element: <Shell />,
        ...boundary,
        children: [
          {
            index: true,
            element: <GroupDashboard />,
            ...boundary,
            handle: {
              crumbs: ["Hub", "Group Dashboard"],
              help: "Group-wide volumes and the state of every company's connection. Figures cover the last 24 hours.",
            },
          },
          {
            path: "companies/onboard",
            element: <OnboardWizard />,
            ...boundary,
            handle: { crumbs: ["Hub", "Companies", "Onboard"] },
          },
          { path: "companies", element: <CompaniesList />, ...boundary, handle: { crumbs: ["Hub", "Companies"] } },
          { path: "companies/:entityId", element: <CompanyDetail />, ...boundary, handle: { crumbs: ["Hub", "Companies"] } },
          { path: "documents", element: <DocumentsQueue />, ...boundary, handle: { crumbs: ["Hub", "Documents"] } },
          { path: "documents/:docId", element: <DocumentInspector />, ...boundary, handle: { crumbs: ["Hub", "Documents"] } },
          { path: "inbound", element: <InboundPage />, ...boundary, handle: { crumbs: ["Hub", "Inbound"] } },
          {
            path: "inbound/:docId",
            element: <InboundDocumentPage />,
            ...boundary,
            handle: { crumbs: ["Hub", "Inbound"] },
          },
          { path: "reports", element: <ReportsPage />, ...boundary, handle: { crumbs: ["Hub", "Reports"] } },
          { path: "users", element: <UsersPage />, ...boundary, handle: { crumbs: ["Hub", "Users & Access"] } },
          { path: "settings", element: <SettingsPage />, ...boundary, handle: { crumbs: ["Hub", "Settings"] } },
          {
            path: "settings/theme",
            element: <ThemeSettingsPage />,
            ...boundary,
            handle: { crumbs: ["Hub", "Theme Settings"] },
          },
          {
            path: "mapping/:entityId/:family/:version",
            element: <MappingStudio />,
            ...boundary,
            handle: { crumbs: ["Hub", "Companies", "Mapping"] },
          },
          // The pre-family URL: react-router ranks the longer static match, so both patterns coexist.
          { path: "mapping/:entityId/:version", element: <LegacyMappingRedirect />, ...boundary },
        ],
      },
    ],
  },
  { path: "*", element: <NotFound /> },
];

export const router = createBrowserRouter(routes);

export type RouteGuard = "group_admin" | "entity_user" | "none";
export type RoutePath = { path: string; guard: RouteGuard };

function guardOf(route: RouteObject): RouteGuard | null {
  if (!isValidElement(route.element)) return null;
  if (route.element.type === RequireRole) {
    const { role } = route.element.props as { role: Role };
    if (role !== "group_admin") throw new Error(`routePaths: no walk persona for RequireRole role=${role}`);
    return "group_admin";
  }
  if (route.element.type === RequireEntityUser) return "entity_user";
  return null;
}

function joinPath(base: string, segment: string): string {
  return `${base.replace(/\/$/, "")}/${segment.replace(/^\//, "")}`;
}

/** Every navigable path in the table above with the guard that wraps it; layout routes contribute nothing.
    A pathed parent that renders its own screen (an element, no index child) is emitted as well. */
export function routePaths(list: RouteObject[] = routes, base = "/", guard: RouteGuard = "none"): RoutePath[] {
  return list.flatMap((route) => {
    const own = guardOf(route) ?? guard;
    const path = route.path ? joinPath(base, route.path) : base;
    if (route.children) {
      const nested = routePaths(route.children, path, own);
      const self = route.path && route.element && !guardOf(route) && !route.children.some((child) => child.index);
      return self ? [{ path, guard: own }, ...nested] : nested;
    }
    return route.index || route.path ? [{ path, guard: own }] : [];
  });
}

export const ROUTE_PATHS: RoutePath[] = routePaths();

